import type { JSX } from 'react'

import { addDays, addMonths, addWeeks, endOfDay, endOfMonth, endOfWeek, format, isBefore, isSameDay, startOfDay, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from 'date-fns'
import { Calendar, ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { LeaveRequestAdminListItemDto } from '@/utils/api/leave-service/models'

import SearchSelect from '@/components/search-select'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser } from '@/lib/role-guards'
import { useLeaveRequestsByScope } from '@/utils/api/leave-service'
import { useListDepartments } from '@/utils/api/user-service'

import { EmployeeRow } from '../components/EmployeeRow'
import { LeaveDetailsDrawer } from '../components/LeaveDetailsDrawer'
import { SubmitLeaveForUserDialog } from '../components/SubmitLeaveForUserDialog'

type ViewMode = 'day' | 'week' | 'month' | '30days'

export interface EmployeeLeaveData {
  userId: number
  userName: string
  userAvatar?: string
  leaves: LeaveRequestAdminListItemDto[]
}

export function LeaveCalendarPage() {
  const { user } = useAuth()
  const isAdmin = isAdminUser(user)

  const [currentDate, setCurrentDate] = useState(() => new Date())
  const today = startOfDay(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | undefined>()
  const [selectedLeaveId, setSelectedLeaveId] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false)
  const scrollAreaRef = useRef<HTMLDivElement | null>(null)

  // Get start and end dates for API query based on view mode
  const { startDate, endDate, displayLabel } = useMemo(() => {
    let start: Date
    let end: Date
    let label: string

    switch (viewMode) {
      case 'day':
        start = startOfDay(currentDate)
        end = endOfDay(currentDate)
        label = format(currentDate, 'MMMM dd, yyyy')
        break
      case 'week':
        start = startOfWeek(currentDate, { weekStartsOn: 1 }) // Monday
        end = endOfWeek(currentDate, { weekStartsOn: 1 })
        label = `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`
        break
      case '30days':
        start = startOfDay(currentDate)
        end = addDays(start, 29)
        label = `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`
        break
      case 'month':
      default:
        start = startOfMonth(currentDate)
        end = endOfMonth(currentDate)
        label = format(currentDate, 'MMMM yyyy')
    }

    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
      displayLabel: label,
    }
  }, [currentDate, viewMode])

  // Fetch leave requests
  const { data: leaveRequestsData, isLoading: isLoadingLeaves } = useLeaveRequestsByScope(
    {
      scope: isAdmin ? 'all' : 'organisation',
      from: startDate,
      to: endDate,
      pageable: { page: 0, size: 1000 },
    },
    {
      query: {
        staleTime: 30_000,
      },
    },
  )

  // Fetch departments for filter
  const { data: departmentsData } = useListDepartments(
    { page: 0, size: 100 },
    {
      query: {
        staleTime: 300_000,
      },
    },
  )

  const activeDepartmentId = selectedDepartmentId
  const leaveRequests = useMemo(() => {
    const content = leaveRequestsData?.data?.content ?? []
    if (!activeDepartmentId) {
      return content
    }
    return content.filter(leave => leave.departmentId === activeDepartmentId)
  }, [activeDepartmentId, leaveRequestsData])
  const departments = useMemo(() => departmentsData?.data?.content ?? [], [departmentsData])

  // Group leaves by employee
  const employeeLeaves = useMemo(() => {
    const grouped = new Map<number, EmployeeLeaveData>()

    leaveRequests.forEach((leave) => {
      if (!leave.applicantUserId || !leave.applicantName) {
        return
      }

      const existing = grouped.get(leave.applicantUserId)
      if (!existing) {
        grouped.set(leave.applicantUserId, {
          userId: leave.applicantUserId,
          userName: leave.applicantName,
          userAvatar: leave.applicantProfilePicUrl,
          leaves: [leave],
        })
        return
      }

      if (!existing.userAvatar && leave.applicantProfilePicUrl) {
        existing.userAvatar = leave.applicantProfilePicUrl
      }

      existing.leaves.push(leave)
    })

    return Array.from(grouped.values()).sort((a, b) =>
      a.userName.localeCompare(b.userName),
    )
  }, [leaveRequests])

  // Generate calendar days based on view mode
  const calendarDays = useMemo(() => {
    const days: Date[] = []
    let start: Date
    let end: Date

    switch (viewMode) {
      case 'day':
        return [currentDate]
      case 'week':
        start = startOfWeek(currentDate, { weekStartsOn: 1 })
        end = endOfWeek(currentDate, { weekStartsOn: 1 })
        break
      case '30days':
        start = startOfDay(currentDate)
        end = addDays(start, 29)
        break
      case 'month':
      default:
        start = startOfMonth(currentDate)
        end = endOfMonth(currentDate)
    }

    let current = start
    while (current <= end) {
      days.push(current)
      current = addDays(current, 1)
    }

    return days
  }, [currentDate, viewMode])

  const dayWidth = useMemo(() => (
    viewMode === 'day' ? 800 : viewMode === 'week' ? 120 : 40
  ), [viewMode])

  useEffect(() => {
    if (isLoadingLeaves) {
      return
    }
    const container = scrollAreaRef.current
    if (!container) {
      return
    }
    const viewport = container.querySelector<HTMLDivElement>('[data-radix-scroll-area-viewport]')
    if (!viewport) {
      return
    }
    const currentIndex = calendarDays.findIndex(day => isSameDay(day, startOfDay(new Date())))
    if (currentIndex === -1) {
      return
    }
    const targetLeft = Math.max(
      0,
      (currentIndex * dayWidth) - (viewport.clientWidth / 2) + (dayWidth / 2),
    )

    requestAnimationFrame(() => {
      viewport.scrollTo({ left: targetLeft, behavior: 'smooth' })
    })
  }, [calendarDays, dayWidth, isLoadingLeaves])

  const handlePrevious = () => {
    setCurrentDate((prev) => {
      switch (viewMode) {
        case 'day':
          return subDays(prev, 1)
        case 'week':
          return subWeeks(prev, 1)
        case '30days':
          return subDays(prev, 30)
        case 'month':
        default:
          return subMonths(prev, 1)
      }
    })
  }

  const handleNext = () => {
    setCurrentDate((prev) => {
      switch (viewMode) {
        case 'day':
          return addDays(prev, 1)
        case 'week':
          return addWeeks(prev, 1)
        case '30days':
          return addDays(prev, 30)
        case 'month':
        default:
          return addMonths(prev, 1)
      }
    })
  }

  const handleLeaveClick = (leaveId: number) => {
    setSelectedLeaveId(leaveId)
    setDrawerOpen(true)
  }

  let employeeContent: JSX.Element
  if (employeeLeaves.length === 0) {
    employeeContent = (
      <Card className="p-8 text-center">
        <Calendar className="mx-auto size-12 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          No leave requests found for this period
        </p>
      </Card>
    )
  }
  else {
    employeeContent = (
      <div className="space-y-2">
        {employeeLeaves.map(employee => (
          <EmployeeRow
            key={employee.userId}
            employee={employee}
            calendarDays={calendarDays}
            dayWidth={dayWidth}
            today={today}
            onLeaveClick={handleLeaveClick}
          />
        ))}
      </div>
    )
  }

  let calendarContent: JSX.Element
  if (isLoadingLeaves) {
    calendarContent = (
      <div className="space-y-2 p-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }
  else {
    calendarContent = (
      <div className="h-full" ref={scrollAreaRef}>
        <ScrollArea className="h-full">
          <div className="min-w-fit p-6">
            {/* Calendar Header - Days */}
            <div className="mb-2 flex">
              <div className="sticky left-0 z-20 w-56 shrink-0 border-r bg-background" />
              {' '}
              {/* Space for employee names */}
              <div className="flex flex-1">
                {calendarDays.map((day) => {
                  const isToday = isSameDay(day, today)
                  const isPastDay = isBefore(day, today)

                  const dayClassName = isToday
                    ? 'bg-primary/40'
                    : isPastDay
                      ? 'bg-gray-300'
                      : ''
                  return (
                    <div
                      key={day.toISOString()}
                      className={`flex flex-col items-center border-l px-1 py-2 first:border-l-0 ${dayClassName}`}
                      style={{ minWidth: `${dayWidth}px` }}
                    >
                      <span
                        className={`text-xs font-medium ${isToday
                          ? 'text-primary'
                          : isPastDay
                            ? 'text-muted-foreground/70'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {format(day, 'EEE')}
                      </span>
                      <span
                        className={`text-sm font-semibold ${isToday
                          ? 'text-primary'
                          : isPastDay
                            ? 'text-muted-foreground/80'
                            : 'text-foreground'
                        }`}
                      >
                        {format(day, 'd')}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Employee Rows */}
            {employeeContent}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Calendar className="size-6 text-primary" />
            <h1 className="text-2xl font-bold">Leave Calendar</h1>
          </div>

          {isAdmin && (
            <Button onClick={() => setSubmitDialogOpen(true)}>
              <Plus className="mr-2 size-4" />
              Request Leave
            </Button>
          )}
        </div>

        {/* Filters and Controls */}
        <div className="mt-4 flex items-center justify-between">
          {/* Department Filter */}
          <div className="flex items-center gap-2">
            <SearchSelect
              label=""
              placeholder={isAdmin ? 'Everyone' : 'All Departments'}
              value={activeDepartmentId ? String(activeDepartmentId) : ''}
              onValueChange={(value) => {
                setSelectedDepartmentId(value ? Number(value) : undefined)
              }}
              options={[
                { id: 0, value: '', name: isAdmin ? 'Everyone' : 'All Departments' },
                ...departments.map(dept => ({
                  id: typeof dept.id === 'number' || typeof dept.id === 'string' ? dept.id : '',
                  value: String(dept.id ?? ''),
                  name: String(dept.name ?? 'Unknown'),
                })),
              ]}
            />
          </div>

          {/* View Mode Tabs */}
          <div className="flex items-center rounded-lg border bg-muted/30">
            {(['day', 'week', 'month', '30days'] as ViewMode[]).map((mode) => {
              const isActive = viewMode === mode
              const buttonStateClass = isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  className={`px-4 py-2 text-sm font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${buttonStateClass}`}
                >
                  {mode === '30days' ? '30 Days' : mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              )
            })}
          </div>
          {/* Month Navigation */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium uppercase tracking-wide">
              {displayLabel}
            </span>
            <Button variant="outline" size="icon" onClick={handlePrevious}>
              <ChevronLeft className="size-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleNext}>
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-hidden">
        {calendarContent}
      </div>

      {/* Dialogs */}
      {submitDialogOpen && (
        <SubmitLeaveForUserDialog
          open={submitDialogOpen}
          onOpenChange={setSubmitDialogOpen}
        />
      )}

      {selectedLeaveId && (
        <LeaveDetailsDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          leaveRequestId={selectedLeaveId}
        />
      )}
    </div>
  )
}
